import DeleteIcon from "@mui/icons-material/Delete";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Stack,
  Box,
  IconButton,
  Tooltip,
  Typography,
  Divider,
} from "@mui/material";
import { useState } from "react";

import type { SxProps, Theme } from "@mui/material/styles";

interface ContextDetails {
  voyages?: string | null;
  vessel_name?: string | null;
  company_name?: string | null;
  type?: string | null;
}

interface DeleteButtonProps {
  onConfirm: () => void;
  disabled?: boolean;
  children?: React.ReactNode;
  name?: string;
  text?: string;
  title?: string;
  buttonName?: string;
  contextDetails?: ContextDetails;
  sx?: SxProps<Theme>;
}

const DeleteButton = ({
  onConfirm,
  disabled = false,
  children,
  name,
  text,
  title = "Delete",
  buttonName,
  contextDetails = {},
  sx,
}: DeleteButtonProps) => {
  const [open, setOpen] = useState(false);

  // Open the dialog
  const handleOpen = () => {
    setOpen(true);
  };

  // Close the dialog
  const handleClose = () => {
    setOpen(false);
  };

  const handleDelete = () => {
    onConfirm();
    handleClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "hidden",
          }
        }}
        sx={sx}
      >
        {/* Header Bar with Icon and Title */}
        <Box
          sx={{
            bgcolor: "error.lighter",
            py: 2.5,
            px: 3,
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Box 
            sx={{ 
              bgcolor: "background.paper",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 48,
              height: 48,
              boxShadow: 1,
              flexShrink: 0,
            }}
          >
            <DeleteIcon sx={{ fontSize: 24, color: "error.main" }} />
          </Box>
          <DialogTitle 
            sx={{ 
              fontWeight: 600, 
              fontSize: "1.25rem",
              p: 0,
              color: "error.dark",
            }}
          >
            Confirm Deletion
          </DialogTitle>
        </Box>

        <DialogContent sx={{ pb: 1, pt: 3, px: 3 }}>
          <DialogContentText 
            align="center"
            sx={{ 
              color: "text.secondary",
              fontSize: "0.95rem",
              lineHeight: 1.6,
            }}
          >
            {text ?? "Are you sure you want to delete"}
            {name ? (
              <>
                {" "}
                <Box 
                  component="span" 
                  sx={{ 
                    fontWeight: 600, 
                    color: "text.primary" 
                  }}
                >
                  "{name}"
                </Box>
              </>
            ) : ""}
            ?
          </DialogContentText>
          {contextDetails && Object.keys(contextDetails).length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ px: 1 }}>
                <Stack spacing={1.5}>
                  {contextDetails.vessel_name && contextDetails.voyages && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 24,
                      }}
                    >
                      <Typography
                        sx={{
                          textAlign: "center",
                          color: "text.primary",
                          fontWeight: 500,
                        }}
                      >
                        {contextDetails.vessel_name}{" "}
                        <em>v.{contextDetails.voyages}</em>
                      </Typography>
                    </Box>
                  )}

                  {contextDetails.company_name && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        minHeight: 24,
                        px: 1,
                      }}
                    >
                      <Typography
                        color="text.secondary"
                        sx={{ fontSize: "0.90rem" }}
                      >
                        Company:
                      </Typography>
                      <Typography
                        color="text.primary"
                        sx={{
                          maxWidth: "65%",
                          textAlign: "right",
                          fontSize: "0.90rem",
                          fontWeight: 500,
                        }}
                      >
                        {contextDetails.company_name}
                      </Typography>
                    </Box>
                  )}

                  {contextDetails.type && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        minHeight: 24,
                        px: 1,
                      }}
                    >
                      <Typography
                        color="text.secondary"
                        sx={{ fontSize: "0.90rem" }}
                      >
                        Type:
                      </Typography>
                      <Typography
                        color="text.primary"
                        sx={{
                          maxWidth: "65%",
                          textAlign: "right",
                          fontSize: "0.90rem",
                          fontWeight: 500,
                        }}
                      >
                        {contextDetails.type}
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            </>
          )}
          <DialogContentText 
            align="center"
            sx={{ 
              color: "text.disabled",
              fontSize: "0.85rem",
              mt: 1,
            }}
          >
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 1.5, px: 3, pb: 3, pt: 2 }}>
          <Button
            onClick={handleClose}
            variant="outlined"
            size="large"
            sx={{
              minWidth: 100,
              textTransform: "none",
              fontWeight: 500,
              borderColor: "divider",
              color: "text.primary",
              "&:hover": {
                borderColor: "text.secondary",
                bgcolor: "action.hover",
              },
            }}
          >
            Cancel
          </Button>

          <Button 
            onClick={handleDelete} 
            variant="contained" 
            color="error"
            size="large"
            sx={{
              minWidth: 100,
              textTransform: "none",
              fontWeight: 500,
              boxShadow: "none",
              "&:hover": {
                boxShadow: "none",
              },
            }}
          >
            {buttonName ?? "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      {children ? (
        <Box onClick={disabled ? undefined : handleOpen}>{children}</Box>
      ) : (
        <Tooltip title={title}>
          <IconButton
            aria-label="delete"
            disabled={disabled}
            onClick={handleOpen}
            size="small"
          >
            <DeleteIcon sx={{ fontSize: "1.2rem" }} />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

export default DeleteButton;
